import { useMemo, useState } from 'react'
import type { OrderResponse, OrderStatus } from '@/services/ordersApi'
import type { OrderKanbanColumn } from '@/lib/verticalUI'
import { AlertCircle, Loader2, ShoppingCart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import OrderTicketCard from './OrderTicketCard'
import { useNow, type WaitThresholds } from './useTicketTimer'

interface OrdersKanbanBoardProps {
  orders: readonly OrderResponse[]
  columns: readonly OrderKanbanColumn[]
  thresholds: WaitThresholds
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  primaryActionFor: (status: OrderStatus) => { label: string; next: OrderStatus } | null
  updatingOrderId: string | null
  onOpen: (order: OrderResponse) => void
  onAdvance: (order: OrderResponse, next: OrderStatus) => void
  onEdit: (order: OrderResponse) => void
  onNotify: (order: OrderResponse) => void
  onCancel: (order: OrderResponse) => void
  onCreate: () => void
  onViewHistory: () => void
}

function isToday(value: string): boolean {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return false
  const now = new Date()
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  )
}

export default function OrdersKanbanBoard({
  orders,
  columns,
  thresholds,
  isLoading,
  isError,
  onRetry,
  primaryActionFor,
  updatingOrderId,
  onOpen,
  onAdvance,
  onEdit,
  onNotify,
  onCancel,
  onCreate,
  onViewHistory,
}: OrdersKanbanBoardProps) {
  const now = useNow(30_000)
  const [mobileColumn, setMobileColumn] = useState<string>(columns[0]?.id ?? 'new')

  const byColumn = useMemo(() => {
    const map = new Map<string, OrderResponse[]>()
    for (const column of columns) {
      const matching = orders
        .filter((order) => column.statuses.includes(order.status))
        .filter((order) => (column.completedTodayOnly ? isToday(order.completedAt || order.updatedAt) : true))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      map.set(column.id, matching)
    }
    return map
  }, [orders, columns])

  if (isError) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-rose-400" />
        <p className="mt-3 text-sm font-semibold text-rose-900">No pudimos cargar las comandas</p>
        <p className="mt-1 text-sm text-rose-700">Revisa la conexión o vuelve a intentar.</p>
        <Button type="button" variant="outline" className="mt-4" onClick={onRetry}>
          Reintentar
        </Button>
      </div>
    )
  }

  if (isLoading && orders.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center text-slate-500" role="status">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando comandas...
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center">
        <ShoppingCart className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 text-sm font-semibold text-slate-800">No hay comandas con estos filtros</p>
        <p className="mt-1 text-sm text-slate-600">Cuando entre un pedido por WhatsApp aparecerá aquí para aceptarlo.</p>
        <Button type="button" className="mt-4 bg-brand text-white hover:bg-brand-dark" onClick={onCreate}>
          Crear comanda manual
        </Button>
      </div>
    )
  }

  const renderColumn = (column: OrderKanbanColumn, compact = false) => {
    const items = byColumn.get(column.id) ?? []
    return (
      <section
        key={column.id}
        className={[
          'flex min-h-0 flex-col rounded-2xl border border-slate-200 bg-slate-50/70',
          compact ? '' : 'max-h-[calc(100svh-16rem)]',
        ].join(' ')}
        aria-label={`${column.label} (${items.length})`}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 px-3 py-2.5">
          <h3 className="text-sm font-bold text-slate-800">{column.label}</h3>
          <div className="flex items-center gap-2">
            {column.completedTodayOnly ? (
              <button
                type="button"
                onClick={onViewHistory}
                className="text-[11px] font-semibold text-brand underline-offset-2 hover:underline"
              >
                Ver historial
              </button>
            ) : null}
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums text-slate-600 shadow-sm">
              {items.length}
            </span>
          </div>
        </header>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 pb-2">
          {items.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 bg-white/60 px-3 py-6 text-center text-xs text-slate-500">
              Sin comandas
            </p>
          ) : (
            items.map((order) => (
              <OrderTicketCard
                key={order.id}
                order={order}
                now={now}
                thresholds={thresholds}
                primaryAction={primaryActionFor(order.status)}
                isUpdating={updatingOrderId === order.id}
                onOpen={onOpen}
                onAdvance={onAdvance}
                onEdit={onEdit}
                onNotify={onNotify}
                onCancel={onCancel}
              />
            ))
          )}
        </div>
      </section>
    )
  }

  return (
    <>
      <div className="hidden gap-3 lg:grid lg:grid-cols-4">{columns.map((column) => renderColumn(column))}</div>

      <div className="lg:hidden">
        <div role="tablist" aria-label="Columnas del tablero" className="flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1">
          {columns.map((column) => {
            const count = (byColumn.get(column.id) ?? []).length
            const active = mobileColumn === column.id
            return (
              <button
                key={column.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMobileColumn(column.id)}
                className={[
                  'inline-flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold',
                  active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600',
                ].join(' ')}
              >
                {column.label}
                <span className={['rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums', active ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600'].join(' ')}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
        <div className="mt-2">
          {columns.filter((column) => column.id === mobileColumn).map((column) => renderColumn(column, true))}
        </div>
      </div>
    </>
  )
}
